import { createFileRoute } from "@tanstack/react-router";
// From the types barrel, NOT the feature barrel. `validateSearch` runs during routing, before the
// lazily-split screen loads, so whatever this file imports lands in the INITIAL bundle. Reaching
// through `@modules/rounds/configuration` pulled the hooks module and with it
// `@tanstack/react-form` — 45 kB gzip of wizard preloaded on every page, login included, for a
// four-line string narrowing.
import {
  toBuilderView,
  type BuilderView,
} from "@modules/rounds/configuration/types";
import { RoundSetupScreen } from "@modules/rounds/views";
import { requireSurface } from "./-guards";

interface SetupSearch {
  /**
   * Which builder view to open. In the URL so a reload resumes where it left off — and so
   * « Vérifier et planifier » is a linkable destination rather than modal state.
   */
  step: BuilderView;
}

// /staff/rounds/$roundId/setup — the round builder reopened on an existing round.
//
// Same shape as the create route: `staff_` opts out of the /staff layout because the design gives
// the builder its own full-width chrome, and escaping the layout also escapes its guard, so the
// surface check runs here.
//
// The id lives in the PATH rather than component state, which is what makes a refresh mid-wizard
// resume the round instead of restarting an empty one — and what lets "Continue setup" on a DRAFT
// card link straight in.
export const Route = createFileRoute("/staff_/rounds/$roundId/setup")({
  // Hand-written rather than a Zod adapter: one enum-ish value does not justify pulling the
  // zod-adapter version question into the router (see the deferred seam in CLAUDE.local.md).
  validateSearch: (search: Record<string, unknown>): SetupSearch => ({
    step: toBuilderView(search.step),
  }),
  beforeLoad: ({ context, location }) =>
    requireSurface(
      context.queryClient,
      "round_configuration",
      location.pathname
    ),
  component: RoundSetupRoute,
});

function RoundSetupRoute() {
  const { roundId } = Route.useParams();
  const { step } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <RoundSetupScreen
      roundId={roundId}
      step={step}
      // `replace` so the browser Back button leaves the wizard instead of walking back through
      // every step the administrator visited.
      onStepChange={(next) => {
        void navigate({ search: { step: next }, replace: true });
      }}
    />
  );
}
